import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppSettings } from '../../config/app-settings';
import { useFormik } from "formik";
import * as Yup from "yup";
import { useUpdateVideoStatsMutation } from "../../redux/Api/VideoApiSlice";
import { Card, CardBody, CardFooter, CardHeader } from "../../components/other/card/card";
import { getUserId } from "../../helpers/auth";
import { toast } from "react-toastify";
import Loader from '../pages/loader';
import { can } from '../../helpers/auth';
import NoPermission from '../pages/noPermission';
import { cleanExcelFile } from "../../components/other/cleanExcelFile";
import generateExcel from "../../helpers/generateExcel";

function UpdateLinkDuration() {
	const [updateVideoStats, { isLoading }] = useUpdateVideoStatsMutation();
	const filePicker = useRef(null);
	const [file, setFile] = useState(null);

	const context = useContext(AppSettings);

	useEffect(() => {
		context.setAppHeaderNone(false);
		context.setAppSidebarNone(false);
		context.setAppContentClass('');
		context.setAppThemePanal(true);

		return function cleanUp() {
			context.setAppHeaderNone(true);
			context.setAppSidebarNone(true);
			context.setAppContentClass('p-0');
			context.setAppThemePanal(false);
		};
	}, []);

	const validation = useFormik({
		enableReinitialize: true,
		initialValues: {
			user: getUserId(),
			file: null,
			stat_type: 'duration',
		},
		validationSchema: Yup.object({
			file: Yup.mixed().required("Excel or CSV file is required"),
		}),
		onSubmit: async (values) => {
			if (!file) {
				toast.error("Please upload a file first.");
				return;
			}

			try {
				toast.info("Cleaning file before upload...");
				const cleanedFile = await cleanExcelFile(file);

				const formData = new FormData();
				formData.append("file", cleanedFile);
				formData.append("user", values.user);
				formData.append("stat_type", values.stat_type);

				toast.info("Uploading file, please wait...");
				const response = await updateVideoStats(formData).unwrap();

				if (response?.message) {
					toast.success(response.message);
				}
				if (response?.updated_count !== undefined) {
					toast.info(`Updated ${response.updated_count} link(s)`);
				}

				const hasMissingLinks = response?.missing_links && response.missing_links.length > 0;
				const hasIgnoredLinks = response?.ignored_duration_links && response.ignored_duration_links.length > 0;

				if (hasMissingLinks || hasIgnoredLinks) {
					const failedLinks = [];

					if (hasMissingLinks) {
						toast.warning(`Missing links: ${response.missing_links.length}`);
						response.missing_links.forEach(link => {
							failedLinks.push({ link, reason: 'Missing Link' });
						});
					}

					if (hasIgnoredLinks) {
						toast.warning(`Ignored duration links: ${response.ignored_duration_links.length}`);
						response.ignored_duration_links.forEach(link => {
							failedLinks.push({ link, reason: 'Ignored Duration' });
						});
					}

					generateExcel(failedLinks, `failed_links_${new Date().toISOString().split('T')[0]}`);
					toast.info("Failed links downloaded as Excel file");
				}

				handleReset();
			} catch (error) {
				if (error?.data) {
					const errorMessages = Object.values(error.data).flat();
					errorMessages.forEach((message) => toast.error(message));
				} else {
					toast.error("Failed to upload file");
				}
			}
		},
	});


	const handleFile = (e) => {
		const uploadedFile = e.target.files?.[0];
		if (!uploadedFile) return;
		setFile(uploadedFile);
		validation.setFieldValue("file", uploadedFile);
	};

	const handleReset = () => {
		setFile(null);
		validation.resetForm();
		if (filePicker.current) {
			filePicker.current.value = "";
		}
	};

	if (!can("change_videoduration")) {
		return <NoPermission />
	}

	return (
		<div className='flex flex-col gap-8'>
			<Loader isLoading={isLoading} />
			<Card>
				<CardHeader>Update Link Duration</CardHeader>
				<CardBody>
					<div className="mb-3">
						<label htmlFor="file">Upload CSV or Excel file</label>
						<br />
						<input
							ref={filePicker}
							type="file"
							onChange={handleFile}
							accept=".xlsx, .xls, .csv"
							name="file"
							id="file"
						/>
						{validation.errors.file && validation.touched.file && (
							<p className="text-danger mt-4">{validation.errors.file}</p>
						)}
						{file && (
							<p className="mt-2 text-success">
								Selected file: <strong>{file.name}</strong>
							</p>
						)}
					</div>
				</CardBody>
				<CardFooter>
					<div className="flex gap-4">
						<button
							type="submit"
							className="btn btn-outline-theme"
							onClick={validation.handleSubmit}
							disabled={isLoading}
						>
							Submit
						</button>
						<button
							type="button"
							className="btn btn-outline-warning"
							onClick={handleReset}
							disabled={isLoading}
						>
							Reset
						</button>
					</div>
				</CardFooter>
			</Card>
		</div>
	);
}

export default UpdateLinkDuration;
